import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EmployeeAvailabilityMatrix = () => {
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [viewMode, setViewMode] = useState('week'); // week, day

  const employees = [
    {
      id: "EMP001",
      name: "Anna Müller",
      department: "Customer Service",
      status: "available",
      currentShift: null,
      nextShift: "14:00-22:00",
      skills: ["Phone Support", "Chat"],
      availability: {
        monday: { morning: true, afternoon: true, night: false },
        tuesday: { morning: true, afternoon: true, night: false },
        wednesday: { morning: true, afternoon: false, night: false },
        thursday: { morning: true, afternoon: true, night: false },
        friday: { morning: true, afternoon: true, night: false },
        saturday: { morning: false, afternoon: false, night: false },
        sunday: { morning: false, afternoon: false, night: false }
      }
    },
    {
      id: "EMP002",
      name: "Thomas Weber",
      department: "Operations",
      status: "working",
      currentShift: "06:00-14:00",
      nextShift: null,
      skills: ["Logistics", "Quality Control"],
      availability: {
        monday: { morning: true, afternoon: false, night: false },
        tuesday: { morning: true, afternoon: false, night: false },
        wednesday: { morning: true, afternoon: false, night: false },
        thursday: { morning: true, afternoon: false, night: false },
        friday: { morning: true, afternoon: false, night: false },
        saturday: { morning: true, afternoon: false, night: false },
        sunday: { morning: false, afternoon: false, night: false }
      }
    },
    {
      id: "EMP003",
      name: "Maria Schmidt",
      department: "Customer Service",
      status: "unavailable",
      currentShift: null,
      nextShift: "14:00-22:00",
      skills: ["Email Support", "Technical"],
      availability: {
        monday: { morning: false, afternoon: false, night: false },
        tuesday: { morning: false, afternoon: true, night: true },
        wednesday: { morning: false, afternoon: true, night: true },
        thursday: { morning: false, afternoon: true, night: true },
        friday: { morning: false, afternoon: true, night: true },
        saturday: { morning: false, afternoon: false, night: false },
        sunday: { morning: false, afternoon: false, night: false }
      }
    },
    {
      id: "EMP004",
      name: "Klaus Fischer",
      department: "Operations",
      status: "break",
      currentShift: "14:00-22:00",
      nextShift: null,
      skills: ["Maintenance", "Safety"],
      availability: {
        monday: { morning: false, afternoon: true, night: true },
        tuesday: { morning: false, afternoon: true, night: true },
        wednesday: { morning: false, afternoon: true, night: true },
        thursday: { morning: false, afternoon: true, night: true },
        friday: { morning: false, afternoon: true, night: true },
        saturday: { morning: false, afternoon: false, night: false },
        sunday: { morning: false, afternoon: false, night: false }
      }
    },
    {
      id: "EMP005",
      name: "Sarah Bauer",
      department: "Manufacturing",
      status: "available",
      currentShift: null,
      nextShift: "22:00-06:00",
      skills: ["Assembly", "Quality Check"],
      availability: {
        monday: { morning: false, afternoon: false, night: true },
        tuesday: { morning: false, afternoon: false, night: true },
        wednesday: { morning: false, afternoon: false, night: true },
        thursday: { morning: false, afternoon: false, night: true },
        friday: { morning: false, afternoon: false, night: true },
        saturday: { morning: false, afternoon: false, night: true },
        sunday: { morning: false, afternoon: false, night: false }
      }
    },
    {
      id: "EMP006",
      name: "Michael König",
      department: "Operations",
      status: "working",
      currentShift: "22:00-06:00",
      nextShift: null,
      skills: ["Security", "Monitoring"],
      availability: {
        monday: { morning: false, afternoon: false, night: true },
        tuesday: { morning: false, afternoon: false, night: true },
        wednesday: { morning: false, afternoon: false, night: true },
        thursday: { morning: false, afternoon: false, night: true },
        friday: { morning: false, afternoon: false, night: true },
        saturday: { morning: false, afternoon: false, night: true },
        sunday: { morning: false, afternoon: false, night: true }
      }
    }
  ];

  const departments = ['all', 'Customer Service', 'Operations', 'Manufacturing'];
  const weekDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const dayLabels = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
  const shiftPeriods = ['morning', 'afternoon', 'night'];
  const periodLabels = ['Früh', 'Spät', 'Nacht'];

  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
        return 'text-success bg-success/10 border-success/20';
      case 'working':
        return 'text-primary bg-primary/10 border-primary/20';
      case 'break':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'unavailable':
        return 'text-error bg-error/10 border-error/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'available':
        return 'CheckCircle';
      case 'working':
        return 'Clock';
      case 'break':
        return 'Coffee';
      case 'unavailable':
        return 'XCircle';
      default:
        return 'Circle';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'available':
        return 'Verfügbar';
      case 'working':
        return 'Arbeitet';
      case 'break':
        return 'Pause';
      case 'unavailable':
        return 'Nicht verfügbar';
      default:
        return 'Unbekannt';
    }
  };

  const filteredEmployees = selectedDepartment === 'all' 
    ? employees 
    : employees?.filter(emp => emp?.department === selectedDepartment);

  return (
    <div className="bg-card border border-border rounded-lg p-6 h-full">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-secondary/10 rounded-lg flex items-center justify-center">
            <Icon name="UserCheck" size={18} className="text-secondary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Mitarbeiterverfügbarkeit</h3>
        </div>
        <div className="flex items-center space-x-2">
          <select
            value={selectedDepartment}
            onChange={(e) => setSelectedDepartment(e?.target?.value)}
            className="text-sm border border-border rounded-md px-2 py-1 bg-background"
          >
            {departments?.map(dept => (
              <option key={dept} value={dept}>
                {dept === 'all' ? 'Alle Abteilungen' : dept}
              </option>
            ))}
          </select>
          <Button
            variant={viewMode === 'week' ? 'default' : 'ghost'}
            size="xs"
            onClick={() => setViewMode('week')}
          >
            Woche
          </Button>
          <Button
            variant={viewMode === 'day' ? 'default' : 'ghost'}
            size="xs"
            onClick={() => setViewMode('day')}
          >
            Tag
          </Button>
        </div>
      </div>
      <div className="space-y-4 max-h-[400px] overflow-y-auto">
        {filteredEmployees?.map((employee) => (
          <div key={employee?.id} className="border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                  <Icon name="User" size={16} className="text-muted-foreground" />
                </div>
                <div>
                  <h4 className="font-medium text-foreground">{employee?.name}</h4>
                  <p className="text-xs text-muted-foreground">{employee?.department}</p>
                </div>
              </div>
              <div className={`px-2 py-1 rounded-md border text-xs font-medium flex items-center space-x-1 ${getStatusColor(employee?.status)}`}>
                <Icon name={getStatusIcon(employee?.status)} size={12} />
                <span>{getStatusLabel(employee?.status)}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-3 text-xs">
              <div>
                <span className="text-muted-foreground">Aktuelle Schicht: </span>
                <span className="font-medium text-foreground">
                  {employee?.currentShift || 'Keine'}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Nächste Schicht: </span>
                <span className="font-medium text-foreground">
                  {employee?.nextShift || 'Keine'}
                </span>
              </div>
            </div>

            <div className="mb-3">
              <div className="text-xs text-muted-foreground mb-1">Fähigkeiten:</div>
              <div className="flex flex-wrap gap-1">
                {employee?.skills?.map((skill, index) => (
                  <span key={index} className="px-2 py-1 bg-muted text-xs rounded-md">
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Wochenverfügbarkeit
              </div>
              <div className="grid grid-cols-7 gap-1">
                {weekDays?.map((day, dayIndex) => (
                  <div key={day} className="text-center">
                    <div className="text-xs font-medium text-muted-foreground mb-1">
                      {dayLabels?.[dayIndex]}
                    </div>
                    <div className="space-y-1">
                      {shiftPeriods?.map((period, periodIndex) => (
                        <div
                          key={period}
                          className={`w-full h-6 rounded text-xs flex items-center justify-center ${
                            employee?.availability?.[day]?.[period]
                              ? 'bg-success/20 text-success border border-success/30' :'bg-muted text-muted-foreground'
                          }`}
                          title={`${dayLabels?.[dayIndex]} ${periodLabels?.[periodIndex]}`}
                        >
                          {periodLabels?.[periodIndex]?.[0]}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6 pt-4 border-t border-border">
        <div className="grid grid-cols-4 gap-4 text-center text-xs">
          <div>
            <div className="text-lg font-semibold text-success">
              {filteredEmployees?.filter(emp => emp?.status === 'available')?.length}
            </div>
            <div className="text-muted-foreground">Verfügbar</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-primary">
              {filteredEmployees?.filter(emp => emp?.status === 'working')?.length}
            </div>
            <div className="text-muted-foreground">Arbeitet</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-warning">
              {filteredEmployees?.filter(emp => emp?.status === 'break')?.length}
            </div>
            <div className="text-muted-foreground">Pause</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-error">
              {filteredEmployees?.filter(emp => emp?.status === 'unavailable')?.length}
            </div>
            <div className="text-muted-foreground">Nicht verfügbar</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeAvailabilityMatrix;